<template>
    <div class="monitoring-kuli-page">
        <section class="monitoring-kuli-card monitoring-kuli-search-card">
            <label for="monitoring-kuli-search">Cari Nama Kuli</label>
            <input
                id="monitoring-kuli-search"
                v-model="search"
                type="search"
                placeholder="Cari nama kuli..."
                :disabled="isLoading"
                @keyup.enter.prevent="loadKuli"
            >
            <button
                class="monitoring-kuli-button monitoring-kuli-button--primary"
                type="button"
                :disabled="isLoading"
                @click="loadKuli"
            >
                {{ isLoading ? 'Loading...' : 'CARI' }}
            </button>
        </section>

        <section class="monitoring-kuli-card monitoring-kuli-list-card">
            <header class="monitoring-kuli-list-card__header">
                <h1>List Kuli</h1>
                <button
                    class="monitoring-kuli-button monitoring-kuli-button--primary"
                    type="button"
                    :disabled="isExporting || isLoading"
                    @click="downloadList"
                >
                    {{ isExporting ? 'Downloading...' : 'Export Data' }}
                </button>
            </header>

            <div class="monitoring-kuli-table-wrap">
                <table class="monitoring-kuli-table">
                    <thead>
                        <tr>
                            <th scope="col" class="monitoring-kuli-table__name-column">
                                <button type="button" class="monitoring-kuli-sort" @click="toggleSort">
                                    Nama Kuli
                                    <span class="monitoring-kuli-sort__arrows" aria-hidden="true">↕</span>
                                    <span class="sr-only">Urutkan nama kuli</span>
                                </button>
                            </th>
                            <th scope="col">Jumlah TO</th>
                            <th scope="col">Jumlah Koli</th>
                            <th scope="col">Jumlah Dus Oli</th>
                            <th scope="col">Jumlah Dus Part</th>
                            <th scope="col">Jumlah Dus Ban</th>
                            <th scope="col">Jumlah Dus Bat</th>
                            <th scope="col" class="monitoring-kuli-table__action-column">Tindakan</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="isLoading">
                            <td colspan="8" class="monitoring-kuli-table__state">Memuat data kuli...</td>
                        </tr>
                        <tr v-else-if="error">
                            <td colspan="8" class="monitoring-kuli-table__state monitoring-kuli-table__state--error">{{ error }}</td>
                        </tr>
                        <tr v-else-if="!sortedRows.length">
                            <td colspan="8" class="monitoring-kuli-table__state">Data kuli tidak ditemukan</td>
                        </tr>
                        <tr v-for="row in sortedRows" v-else :key="row.id || row.nama_kuli">
                            <td>{{ row.nama_kuli }}</td>
                            <td>{{ displayValue(row.jumlah_to) }}</td>
                            <td>{{ displayValue(row.jumlah_koli) }}</td>
                            <td>{{ displayValue(row.jumlah_dus_oli) }}</td>
                            <td>{{ displayValue(row.jumlah_dus_part) }}</td>
                            <td>{{ displayValue(row.jumlah_dus_ban) }}</td>
                            <td>{{ displayValue(row.jumlah_dus_bat) }}</td>
                            <td class="monitoring-kuli-table__action-column">
                                <button class="monitoring-kuli-detail-button" type="button" @click="openDetail(row)">Lihat Detail</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

        <monitoring-kuli-detail-modal
            v-if="detailModal.show"
            :data="detailModal.data"
            :loading="detailModal.loading"
            :error="detailModal.error"
            :exporting="detailModal.exporting"
            @close="closeDetail"
            @export="downloadDetail"
        />
    </div>
</template>

<script>
import MonitoringKuliDetailModal from './MonitoringKuliDetailModal.vue'

export default {
    name: 'MonitoringKuli',
    metaInfo: {
        title: 'Monitoring Kuli'
    },
    components: {
        MonitoringKuliDetailModal
    },
    data() {
        return {
            search: '',
            rows: [],
            isLoading: false,
            isExporting: false,
            error: '',
            sortDirection: 'asc',
            detailModal: {
                show: false,
                data: null,
                loading: false,
                exporting: false,
                error: ''
            }
        }
    },
    computed: {
        sortedRows() {
            return this.rows.slice().sort((left, right) => {
                const comparison = String(left.nama_kuli || '').localeCompare(String(right.nama_kuli || ''), 'id', { sensitivity: 'base' })
                return this.sortDirection === 'asc' ? comparison : -comparison
            })
        }
    },
    mounted() {
        this.loadKuli()
    },
    methods: {
        async loadKuli() {
            this.isLoading = true
            this.error = ''

            try {
                const response = await this.$http.get('/api/monitoring/kuli', {
                    params: { nama: this.search.trim() }
                })
                this.rows = response && response.data && Array.isArray(response.data.data)
                    ? response.data.data
                    : []
            } catch (error) {
                this.rows = []
                this.error = this.errorMessage(error, 'Gagal memuat data kuli')
            } finally {
                this.isLoading = false
            }
        },
        async openDetail(row) {
            this.detailModal.show = true
            this.detailModal.data = row
            this.detailModal.loading = true
            this.detailModal.error = ''

            if (row.id === null || row.id === undefined || row.id === '') {
                this.detailModal.loading = false
                this.detailModal.error = 'Detail kuli tidak memiliki ID.'
                return
            }

            try {
                const response = await this.$http.get(`/api/monitoring/kuli/${encodeURIComponent(row.id)}`)
                this.detailModal.data = response && response.data ? response.data.data : null
            } catch (error) {
                this.detailModal.error = this.errorMessage(error, 'Gagal memuat detail kuli')
            } finally {
                this.detailModal.loading = false
            }
        },
        closeDetail() {
            this.detailModal.show = false
            this.detailModal.data = null
            this.detailModal.loading = false
            this.detailModal.exporting = false
            this.detailModal.error = ''
        },
        async downloadList() {
            this.isExporting = true

            try {
                const response = await this.$http.get('/api/monitoring/kuli/export-excel', {
                    params: { nama: this.search.trim() },
                    responseType: 'blob'
                })
                this.downloadBlob(response, 'monitoring-kuli.xlsx')
            } catch (error) {
                this.notifyError(this.errorMessage(error, 'Gagal mengunduh data kuli'))
            } finally {
                this.isExporting = false
            }
        },
        async downloadDetail() {
            const detail = this.detailModal.data
            if (!detail || detail.id === null || detail.id === undefined || detail.id === '') return

            this.detailModal.exporting = true

            try {
                const response = await this.$http.get(`/api/monitoring/kuli/${encodeURIComponent(detail.id)}/export-excel`, {
                    responseType: 'blob'
                })
                this.downloadBlob(response, `detail-kuli-${this.fileNamePart(detail.nama_kuli)}.xlsx`)
            } catch (error) {
                this.notifyError(this.errorMessage(error, 'Gagal mengunduh detail kuli'))
            } finally {
                this.detailModal.exporting = false
            }
        },
        downloadBlob(response, fileName) {
            const blob = response && response.data !== undefined ? response.data : response
            if (!blob || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return

            const href = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = href
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(href)
        },
        toggleSort() {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        },
        displayValue(value) {
            return value === null || value === undefined || value === '' ? '-' : value
        },
        fileNamePart(value) {
            return String(value || 'kuli')
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/gi, '-')
                .replace(/^-|-$/g, '') || 'kuli'
        },
        errorMessage(error, fallback) {
            return error && error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : fallback
        },
        notifyError(message) {
            if (this.$swal && this.$swal.fire) {
                this.$swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: message
                })
            }
        }
    }
}
</script>

<style scoped>
.monitoring-kuli-page {
    background: #FAFDFF;
    color: #343434;
    font-family: Poppins, sans-serif;
    min-height: calc(100vh - 50px);
    padding: 29px 0 32px;
}

.monitoring-kuli-card {
    background: #FFFFFF;
    border: 1px solid #EFF3F7;
    border-radius: 9px;
    box-shadow: 0 0 3px rgba(0, 0, 0, .16);
    margin: 0 0 28px;
}

.monitoring-kuli-search-card {
    align-items: center;
    display: grid;
    gap: 28px;
    grid-template-columns: 134px minmax(0, 1fr) 131px;
    min-height: 81px;
    padding: 22px 30px;
}

.monitoring-kuli-search-card label {
    color: #343434;
    font: normal normal normal 16px/24px Poppins, sans-serif;
    margin: 0;
    white-space: nowrap;
}

.monitoring-kuli-search-card input {
    border: 1px solid #D5D5D5;
    border-radius: 8px;
    color: #343434;
    font: normal normal normal 16px/24px Inter, Poppins, sans-serif;
    height: 36px;
    min-width: 0;
    padding: 6px 10px;
}

.monitoring-kuli-search-card input::placeholder {
    color: #BBBBBB;
}

.monitoring-kuli-button {
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    font: normal normal 600 14px/21px Poppins, sans-serif;
    min-height: 36px;
    padding: 7px 10px;
}

.monitoring-kuli-button--primary {
    background: #1B698E;
    color: #FFFFFF;
    min-width: 131px;
}

.monitoring-kuli-button:disabled {
    cursor: not-allowed;
    opacity: .65;
}

.monitoring-kuli-list-card {
    min-height: 534px;
    overflow: hidden;
}

.monitoring-kuli-list-card__header {
    align-items: center;
    border-bottom: 1px solid #F1F1F1;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    min-height: 84px;
    padding: 0 30px;
}

.monitoring-kuli-list-card__header h1 {
    color: #343434;
    font: normal normal normal 16px/24px Poppins, sans-serif;
    grid-column: 2;
    margin: 0;
    text-align: center;
}

.monitoring-kuli-list-card__header .monitoring-kuli-button {
    grid-column: 3;
    justify-self: end;
}

.monitoring-kuli-table-wrap {
    height: 448px;
    overflow: auto;
    padding: 0 30px 20px;
}

.monitoring-kuli-table {
    border-collapse: collapse;
    min-width: 988px;
    table-layout: fixed;
    width: 100%;
}

.monitoring-kuli-table th,
.monitoring-kuli-table td {
    border-bottom: 1px solid #F1F1F1;
    color: #343434;
    font: normal normal normal 12px/18px Poppins, sans-serif;
    height: 54px;
    padding: 8px 8px;
    text-align: left;
    vertical-align: middle;
}

.monitoring-kuli-table th {
    height: 80px;
    white-space: nowrap;
}

.monitoring-kuli-table th:nth-child(1) { width: 112px; }
.monitoring-kuli-table th:nth-child(2) { width: 105px; }
.monitoring-kuli-table th:nth-child(3) { width: 109px; }
.monitoring-kuli-table th:nth-child(4) { width: 129px; }
.monitoring-kuli-table th:nth-child(5) { width: 142px; }
.monitoring-kuli-table th:nth-child(6) { width: 140px; }
.monitoring-kuli-table th:nth-child(7) { width: 130px; }
.monitoring-kuli-table th:nth-child(8) { width: 110px; }

.monitoring-kuli-table__action-column {
    text-align: center !important;
}

.monitoring-kuli-sort {
    background: transparent;
    border: 0;
    color: inherit;
    cursor: pointer;
    font: inherit;
    padding: 0;
}

.monitoring-kuli-sort__arrows {
    color: #343434;
    display: inline-block;
    font-size: 13px;
    margin-left: 3px;
    transform: scaleX(.7);
}

.monitoring-kuli-detail-button {
    background: #FFFFFF;
    border: 1px solid #E8E8E8;
    border-radius: 9px;
    color: #343434;
    cursor: pointer;
    font: normal normal normal 12px/18px Poppins, sans-serif;
    height: 32px;
    min-width: 112px;
    padding: 6px 10px;
}

.monitoring-kuli-table__state {
    color: #888888 !important;
    height: 190px !important;
    text-align: center !important;
}

.monitoring-kuli-table__state--error {
    color: #B3261E !important;
}

@media (max-width: 768px) {
    .monitoring-kuli-page {
        padding-top: 16px;
    }

    .monitoring-kuli-search-card {
        gap: 12px;
        grid-template-columns: 1fr 1fr;
        padding: 18px;
    }

    .monitoring-kuli-search-card label,
    .monitoring-kuli-search-card input {
        grid-column: 1 / -1;
    }

    .monitoring-kuli-search-card .monitoring-kuli-button {
        grid-column: 2;
    }

    .monitoring-kuli-list-card__header {
        gap: 12px;
        grid-template-columns: 1fr auto;
        padding: 0 18px;
    }

    .monitoring-kuli-list-card__header h1 {
        grid-column: 1;
        text-align: left;
    }

    .monitoring-kuli-list-card__header .monitoring-kuli-button {
        grid-column: 2;
    }

    .monitoring-kuli-table-wrap {
        padding-left: 18px;
        padding-right: 18px;
    }
}
</style>
